export interface ResultatCalculMontant {
  valeur: number | null;
  erreur: string | null;
}

/** Analyse uniquement des nombres et des opérateurs, sans exécuter de JavaScript. */
export function calculerMontant(saisie: string): ResultatCalculMontant {
  const texte = saisie.trim().replace(/,/g, '.');
  if (!texte) return { valeur: null, erreur: null };
  if (texte.length > 256) return { valeur: null, erreur: 'Le calcul est trop long.' };
  let position = 0;

  function espaces(): void {
    while (/\s/.test(texte[position] ?? '') && position < texte.length) position++;
  }

  function nombre(profondeur: number): number {
    if (profondeur > 32) throw new Error('Le calcul est trop imbriqué.');
    espaces();
    const caractere = texte[position];
    if (caractere === '+' || caractere === '-') {
      position++;
      return (caractere === '-' ? -1 : 1) * nombre(profondeur + 1);
    }
    if (caractere === '(') {
      position++;
      const valeur = somme(profondeur + 1);
      espaces();
      if (texte[position++] !== ')') throw new Error('Parenthèse fermante manquante.');
      return valeur;
    }
    const token = /^(?:\d+(?:\.\d*)?|\.\d+)/.exec(texte.slice(position));
    if (!token) throw new Error('Calcul incomplet ou invalide.');
    position += token[0].length;
    return Number(token[0]);
  }

  function produit(profondeur: number): number {
    let valeur = nombre(profondeur);
    while (true) {
      espaces();
      const operateur = texte[position];
      if (operateur !== '*' && operateur !== '/') return valeur;
      position++;
      const droite = nombre(profondeur);
      if (operateur === '/' && droite === 0) throw new Error('La division par zéro est impossible.');
      valeur = operateur === '*' ? valeur * droite : valeur / droite;
    }
  }

  function somme(profondeur: number): number {
    let valeur = produit(profondeur);
    while (true) {
      espaces();
      const operateur = texte[position];
      if (operateur !== '+' && operateur !== '-') return valeur;
      position++;
      const droite = produit(profondeur);
      valeur = operateur === '+' ? valeur + droite : valeur - droite;
    }
  }

  try {
    const valeur = somme(0);
    espaces();
    if (position !== texte.length) throw new Error('Calcul incomplet ou invalide.');
    if (!Number.isFinite(valeur) || Math.abs(valeur) > Number.MAX_SAFE_INTEGER / 100) {
      throw new Error('Le résultat du calcul est trop grand.');
    }
    // Arrondir une seule fois, à la fin du calcul, au centime le plus proche.
    const centimes = Math.round((Math.abs(valeur) + Number.EPSILON * Math.abs(valeur)) * 100);
    return { valeur: centimes === 0 ? 0 : Math.sign(valeur) * centimes / 100, erreur: null };
  } catch (erreur) {
    return { valeur: null, erreur: erreur instanceof Error ? erreur.message : 'Calcul invalide.' };
  }
}
